import React from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';

const OPTION_LABELS = ['A', 'B', 'C', 'D'];

const emptyQuestion = () => ({
  question: '',
  type: 'mcq',
  options: ['', '', '', ''],
  correctAnswer: 'A',
  explanation: '',
});

/**
 * Admin editor for Practice-step micro assessment MCQs.
 */
const MicroQuizEditor = ({
  questions = [],
  onChange,
  randomizeQuestions = true,
  onRandomizeChange,
}) => {
  const updateQuestion = (index, field, value) => {
    const next = [...questions];
    next[index] = { ...next[index], [field]: value };
    onChange(next);
  };

  const updateOption = (qIndex, optIndex, value) => {
    const next = [...questions];
    const opts = [...(next[qIndex].options || ['', '', '', ''])];
    opts[optIndex] = value;
    next[qIndex] = { ...next[qIndex], options: opts };
    onChange(next);
  };

  const addQuestion = () => onChange([...questions, emptyQuestion()]);

  const removeQuestion = (index) => onChange(questions.filter((_, i) => i !== index));

  return (
    <div className="space-y-4 border-2 border-emerald-200 bg-emerald-50/40 rounded-2xl p-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h4 className="text-sm font-bold text-emerald-800">Micro Assessment Questions</h4>
          <p className="text-xs text-emerald-700/80 mt-0.5">
            MCQs with answer key and explanations. Students see questions in random order when shuffle is on.
          </p>
        </div>
        <label className="inline-flex items-center gap-3 cursor-pointer shrink-0 select-none">
          <span className="text-sm font-semibold text-gray-700">Shuffle questions</span>
          <button
            type="button"
            role="switch"
            aria-checked={randomizeQuestions}
            onClick={() => onRandomizeChange(!randomizeQuestions)}
            className={`w-12 h-6 rounded-full p-0.5 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:ring-offset-1 ${
              randomizeQuestions ? 'bg-emerald-600' : 'bg-gray-300'
            }`}
          >
            <span
              aria-hidden="true"
              className={`block h-5 w-5 rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out ${
                randomizeQuestions ? 'translate-x-6' : 'translate-x-0'
              }`}
            />
          </button>
        </label>
      </div>

      {questions.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-4">No questions yet. Add your first MCQ below.</p>
      )}

      {questions.map((q, qIdx) => (
        <div key={qIdx} className="bg-white border border-emerald-100 rounded-xl p-4 shadow-sm space-y-3">
          <div className="flex items-start gap-2">
            <GripVertical className="w-4 h-4 text-gray-300 mt-2 shrink-0" />
            <div className="flex-1">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Question {qIdx + 1}</label>
              <textarea
                rows={2}
                value={q.question || ''}
                onChange={(e) => updateQuestion(qIdx, 'question', e.target.value)}
                placeholder="Enter the question text…"
                className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400 resize-none"
              />
            </div>
            <button
              type="button"
              onClick={() => removeQuestion(qIdx)}
              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg shrink-0"
              title="Remove question"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-6">
            {OPTION_LABELS.map((label, optIdx) => (
              <div key={label} className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-gray-100 text-gray-600 text-xs font-black flex items-center justify-center shrink-0">
                  {label}
                </span>
                <input
                  type="text"
                  value={(q.options || [])[optIdx] || ''}
                  onChange={(e) => updateOption(qIdx, optIdx, e.target.value)}
                  placeholder={`Option ${label}`}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400"
                />
              </div>
            ))}
          </div>

          <div className="pl-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Correct answer</label>
              <select
                value={q.correctAnswer || 'A'}
                onChange={(e) => updateQuestion(qIdx, 'correctAnswer', e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400"
              >
                {OPTION_LABELS.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-1">
              <label className="text-xs font-bold text-gray-500 uppercase">Explanation (shown after submit)</label>
              <textarea
                rows={2}
                value={q.explanation || ''}
                onChange={(e) => updateQuestion(qIdx, 'explanation', e.target.value)}
                placeholder="Why is this the correct answer?"
                className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400 resize-none"
              />
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addQuestion}
        className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white rounded-xl text-sm font-bold shadow-md shadow-emerald-600/25 hover:shadow-lg hover:shadow-emerald-600/30 transition-all flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" strokeWidth={2.5} />
        Add Question
      </button>
    </div>
  );
};

export default MicroQuizEditor;
